import styled from "styled-components";
import NewContactForm from "./NewContactForm";
import ContactList from "./list/ContactList";
import ContactDetail from "./contact-details/ContactDetail";
import { inquiryCount } from "./functions";

import { useState, useCallback } from "react";
import { useFirestore } from "react-redux-firebase";
import { useContacts, useItems, useComments, useDocuments } from "../../hooks/useContacts";
import { useDataMode } from "../../context/DataContext";
import { seedContacts } from "../../seedData";
import ConfirmDialog from "../common/ConfirmDialog";

import { ArrowLeft, Database, CloudArrowUp } from "phosphor-react";

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Buttons = styled.div``;

const Analytics = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: blue;
  font-size: 10px;
  font-weight: 900;
  opacity: 0.4;
`;

const Inquiries = styled.div`
  font-weight: 900;
  margin-left: 10px;
  padding: 10px 15px;
  box-shadow: 2px 8px 16px -6px rgb(0 0 0 / 9%);
  border-radius: 5px;
  font-size: 10px;
  display: flex;
  align-items: center;
  color: blue;
  border: 1px solid blue;
`;

function ContactControl() {
  const firestore = useFirestore();
  const { useCloudDatabase, toggleDataMode, resetLocalData } = useDataMode();

  const { docs: contacts } = useContacts();
  const { docs: items } = useItems();
  const { docs: comments } = useComments();
  const { docs: documents } = useDocuments();

  const [selectedContact, setSelectedContact] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formVisibleOnPage, setFormVisibleOnPage] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  const handleClick = useCallback(() => {
    if (selectedContact != null) {
      setSelectedContact(null);
      setEditing(false);
    } else {
      setFormVisibleOnPage(!formVisibleOnPage);
    }
  }, [selectedContact, formVisibleOnPage]);

  const handleAddingNewContactToList = useCallback(() => {
    setFormVisibleOnPage(!formVisibleOnPage);
  }, [formVisibleOnPage]);

  const handleChangingSelectedContact = useCallback((id) => {
    const firestoreContact = contacts.filter((contact) => contact.id === id)[0];
    setSelectedContact(firestoreContact);
  }, [contacts]);

  const handleDeletingContact = useCallback((id) => {
    setContactToDelete(id);
    setShowDeleteDialog(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (contactToDelete) {
      if (useCloudDatabase) {
        firestore.delete({ collection: "contacts", doc: contactToDelete });
      }
      // Note: Local data delete is not implemented in demo mode
      // to preserve the demo experience
      setSelectedContact(null);
      setContactToDelete(null);
    }
  }, [contactToDelete, firestore, useCloudDatabase]);

  const handleEditClick = useCallback(() => {
    setEditing(!editing);
  }, [editing]);

  const handleSeedData = useCallback(async () => {
    if (useCloudDatabase) {
      await seedContacts();
    } else {
      resetLocalData();
    }
  }, [useCloudDatabase, resetLocalData]);

  let currentlyVisibleState = null;
  let buttonText = null;
  let editButton = null;
  if (editing) {
    editButton = "Confirm";
  } else {
    editButton = "Edit";
  }
  if (selectedContact != null) {
    currentlyVisibleState = (
      <ContactDetail
        contact={selectedContact}
        onClickingDelete={handleDeletingContact}
        onClickingEdit={handleEditClick}
        items={items.filter((c) => c.contactID === selectedContact.id)}
        comments={comments}
        editing={editing}
        documents={documents.filter((e) => e.contactID === selectedContact.id)}
      />
    );
    buttonText = <ArrowLeft size={20} />;
  } else if (formVisibleOnPage) {
    currentlyVisibleState = (
      <NewContactForm onNewContactCreation={handleAddingNewContactToList} />
    );
    buttonText = <ArrowLeft size={20} />;
    editButton = null;
  } else {
    currentlyVisibleState = (
      <ContactList
        onContactSelection={handleChangingSelectedContact}
        handleNewContactClick={handleClick}
        contacts={contacts}
        onClickDelete={handleDeletingContact}
        comments={comments}
      />
    );
    buttonText = "Create";
    editButton = null;
  }
  return (
    <div className="container">
      <Container>
        <Buttons>
          <button onClick={handleClick}>{buttonText}</button>
          <button
            style={{ display: editButton ? "inline" : "none" }}
            onClick={handleEditClick}
          >
            {editButton}
          </button>
          {!useCloudDatabase && (
            <button onClick={handleSeedData}>Reset Demo Data</button>
          )}
          <button
            onClick={toggleDataMode}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              backgroundColor: useCloudDatabase ? "#3398B8" : "#666"
            }}
          >
            {useCloudDatabase ? (
              <>
                <CloudArrowUp size={16} />
                Cloud Database
              </>
            ) : (
              <>
                <Database size={16} />
                Demo Mode
              </>
            )}
          </button>
        </Buttons>
        <Analytics>
          new inquiries
          <Inquiries>({inquiryCount(contacts, 1)}) daily</Inquiries>
          <Inquiries>({inquiryCount(contacts, 7)}) weekly</Inquiries>
          <Inquiries>({inquiryCount(contacts, 30)}) monthly</Inquiries>
        </Analytics>
      </Container>
      {currentlyVisibleState}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDelete}
        title="Delete Contact"
        message="Are you sure you want to delete this contact? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        requireConfirmation={true}
        confirmationWord="DELETE"
      />
    </div>
  );
}

export default ContactControl;
